import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ListSellerSummaryExportResponse,
    ListSellerSummaryRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    EnumInput,
    mapSupplierUtilizationSortColumn,
    PayablesInput
} from '@lsq-frontend/remote-services/financing-graphql';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { SortDirection } from 'aws-amplify';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';

@Injectable({ providedIn: 'root' })
export class ListSellerSummaryExportService extends ProtoApiService<
    ListSellerSummaryRequest,
    ListSellerSummaryExportResponse
> {
    public requestType = ListSellerSummaryRequest;
    public responseType = ListSellerSummaryExportResponse;
    public endpointName = 'ListSellerSummaryExport';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListSellerSummaryExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(req: ListSellerSummaryRequest): IExportRequest | ListSellerSummaryRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const statuses = req?.sellerEnrollmentStatusFilter?.map((status) => {
                return new EnumInput(status);
            });
            const input: PayablesInput = omitBy(
                {
                    companyFilter: {
                        buyerIds: [req.buyerKey],
                        supplierIds: []
                    },
                    sellerNameFilter: req.sellerNameFilter ?? '',
                    sellerEnrollmentStatusFilter: statuses,
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapSupplierUtilizationSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] ?? SortDirection.DESCENDING
                        )
                    }
                },
                isNil
            );

            const query = jsonToGraphQLQuery({
                supplierUtilizations: {
                    __args: { input },
                    rows: {
                        name: true,
                        buyerKey: true,
                        feeShare: true,
                        sellerId: true,
                        buyerName: true,
                        sellerKey: true,
                        percentUsed: true,
                        totalPaidFees: { cents: true, currencyCode: true },
                        earlyPaidAmount: { cents: true, currencyCode: true },
                        sellerSummaryKey: true,
                        enrollmentStatus: true,
                        openInvoiceAmount: { cents: true, currencyCode: true },
                        buyerPaidEarlyPaymentFeePercentageVisible: true
                    },
                    totals: { supplierCount: true }
                }
            });
            return {
                fileType: 'csv',
                exportType: ExportType.BUYER_SUPPLIERS,
                query: `{${query}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
